import React from 'react';
import marketingTheme from '../themes/marketingTheme';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Schedule as ScheduleIcon,
  GpsFixed as GpsFixedIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { 
  FaTools, 
  FaRoute, 
  FaMapMarkedAlt,
  FaCrosshairs,
  FaTasks,
  FaStopwatch,
  FaShieldAlt
} from 'react-icons/fa';
import { 
  MdLocationOn, 
  MdRefresh, 
  MdTimeline 
} from 'react-icons/md';
import { 
  GiToolbox, 
  GiPathDistance, 
  GiFactoryArm 
} from 'react-icons/gi';
import { 
  HiOutlineLocationMarker, 
  HiOutlineClock 
} from 'react-icons/hi';
import { 
  RiToolsLine, 
  RiTimeLine 
} from 'react-icons/ri';
import SpaghettiAnalyticsVisualization from '../components/SpaghettiAnalyticsVisualization';
import SpatialComputingVisualization from '../components/SpatialComputingVisualization';
import { SmartCTA } from '../components/SmartCTAs';

const SpaghettiAnalytics = () => {
  const features = [
    {
      icon: <FaTools style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Tool-On Time',
      description: 'Visual Inference of Tool-On Time (e.g. Welder Arc-On)'
    },
    {
      icon: <FaCrosshairs style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Repositioning Time',
      description: 'Visual Inference of Repositioning Time (e.g. Time spent climbing a ladder)'
    },
    {
      icon: <MdRefresh style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Reloading Time',
      description: 'Visual Inference of Reloading Time (e.g. Time spent to reload a tool)'
    },
    {
      icon: <FaTasks style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Preparation Time',
      description: 'Visual Inference of Preparation Time (e.g. Time spent to prepare a tool)'
    },
    {
      icon: <GiFactoryArm style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Movement Analysis',
      description: 'Visual Inference of Repositioning Time (e.g. Time spent climbing a ladder)'
    },
    {
      icon: <GiPathDistance style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Spaghetti Analysis',
      description: 'Visual Inference of time spent moving between workstations'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            AI Vision based Spaghetti Analysis
          </Typography>
          <Typography variant="h5" sx={{ 
            color: marketingTheme.palette.primary.main, 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for Work Area Layout Enhancement.
          </Typography>
        </Box>

        {/* Main Content with Case Study */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              Optimisation of excess movement between workstations can improve process efficiency. The system can quantify excess movement between work areas.
            </Typography>

            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img 
                src="/images/spaghetti_analysis.png" 
                alt="AI Vision for Spaghetti Analysis" 
                style={{ 
                  maxWidth: '450px',
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>

            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              A reduction in nonessential movements increases capacity, and enables more tasks to be undertaken with the same level of staff resourcing. Our solution analyses walking movement patterns to provide layout enhancement recommendations.
              <br /><br />
              The system bolsters traditional Lean techniques with 24/7 empirical comprehension of work area layout. Our tool can provide insights on the root causes of waiting and excess motion.
              <br /><br />
              Our tool uses visual perception to provide layout enhancement recommendations, decluttering work area design.
              <br /><br />
              See where time is being lost to unnecessary walking and get practical suggestions for a better floor plan that eliminates wasted motion and waiting.
              <br /><br />
              Get practical layout suggestions based on actual movement data, not guesswork. Reduce walking distances through the facility, eliminate bottlenecks, and improve the flow of work.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              p: 3,
              textAlign: 'center',
              height: 'fit-content'
            }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 4 }}>
                Hypothetical Case Study
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                  $1B
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  COGS (of an ergonomically-intensive workflow)
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold' }}>
                  5%
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  *Reduction In Waiting and Excess Motion
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
                  Yields
                </Typography>
                <Typography variant="h2" sx={{ color: '#1dd1a1', fontWeight: 'bold', mb: 2 }}>
                  $50M
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', mb: 3 }}>
                  Margin Enhancement
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic',
                display: 'block',
                fontSize: '0.7rem'
              }}>
                *Hypothetical, this scenario is provided for illustrative purposes only, the accuracy and applicability of this scenario may be influenced by unique factors inherent to each user's environment and practices.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Spaghetti Analysis Visualization */}
        <Box sx={{ 
          my: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(29, 209, 161, 0.1) 0%, rgba(26, 58, 108, 0.1) 100%)',
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 3
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
            Movement Pattern Analysis
          </Typography>
          {/* <SpaghettiAnalyticsVisualization /> */}
          <SpatialComputingVisualization />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 2, textAlign: 'center' }}>
            Optimising movement paths to reduce excess walking motion and waiting time.
          </Typography>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <SmartCTA
            context="experience-demo"
            fallbackHref="/contact"
            fallbackText="Learn More"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: marketingTheme.palette.primary.main,
              color: '#000',
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              '&:hover': {
                backgroundColor: marketingTheme.palette.primary.dark,
              },
            }}
          />
        </Box>

        {/* Privacy Notice */}
        <Paper sx={{ 
          backgroundColor: 'rgba(29, 209, 161, 0.1)', 
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4, 
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <FaShieldAlt style={{ fontSize: 60, color: '#1dd1a1', flexShrink: 0 }} />
          <Box>
            <Typography variant="h5" sx={{ 
              color: '#1dd1a1',
              fontWeight: 600,
              mb: 2
            }}>
              Pixel Privacy
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6
            }}>
              MotionLogic's camera technology preserves the privacy of individuals in visual scenes.
              We pseudonymise our data, and remove individually identifying features, including blurring faces.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SpaghettiAnalytics;
